import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import MessagePropType from '../definitions/MessagePropType';
import IconPropType from '../definitions/IconPropType';
import Typography from '../definitions/Typography';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import IconMapLazy from '../definitions/IconMapLazy';
import IconChooser from './IconChooser';
import Caption from './Caption';


function CaptionedIcon({
    iconKey,
    message,

    size = 'small',
    colour = Colours.BLACKBERRY,

    iconProps = {},
    iconSize = 'L',

    alignment = 'centre',
    weight = Typography.WEIGHTS.REGULAR,

    className,
    ...otherProps
}) {
    // build a new iconProps object so we never mutate the passed-in one
    iconProps = {
        ...iconProps,
        stroke: iconProps.stroke ?? colour,
        outlineWidth: iconProps.outlineWidth ?? '0',
    };

    if (!CAPTIONED_ICON_SIZES.includes(size)) {
        size = 'small';     // autocorrect to 'small' if invalid size received
    }

    return (
        <MyFlexWrapper
            className={classNames(className, 'rh-display-flex', 'rh-align-items-center', {
                'rh-justify-content-center': alignment === 'centre',
                'rh-justify-content-left': alignment === 'left',
                'rh-justify-content-right': alignment === 'right',
            })}
            size={size}
            alignment={alignment}
            colour={colour}
            weight={weight}

            {...otherProps}
        >
            <IconChooser
                size={iconSize}
                className={classNames({ 'rh-mr-0_5': iconProps.outlineWidth !== '0' })}
                iconKey={iconKey}
                {...iconProps}
            />

            <Caption
                className="caption"
                message={message}
                colour={colour}
                weight={weight}
                data-test-name="captioned-icon-message"
            />
        </MyFlexWrapper>
    );
}

// by setting the Caption element to font-size: 1em, it will match whatever we set here
const CAPTIONED_ICON_TYPOGRAPHIC_SPECS = {
    'extra-small': `
        font-size: ${Sizes.FONTS['3XS']};   // 0.625rem, e.g. 10px
        line-height: 14px;
    `,
    small: `
        font-size: ${Sizes.FONTS['2XS']};   // 0.75rem, e.g. 12px
        line-height: 16px;
    `,
    medium: `
        font-size: ${Sizes.FONTS.S};        // 1rem, e.g. 16px
        line-height: 22px;
    `,
    large: `
        font-size: ${Sizes.FONTS.M};        // 1.25rem, e.g. 20px
        line-height: 20px;
    `,
};
const CAPTIONED_ICON_SIZES = Object.keys(CAPTIONED_ICON_TYPOGRAPHIC_SPECS);
const CAPTIONED_ICON_HORIZONTAL_ALIGNMENT = [ 'left', 'centre', 'right' ];

CaptionedIcon.propTypes = {
    iconKey: PropTypes.oneOf(Object.keys(IconMapLazy)).isRequired,
    message: MessagePropType.isRequired,

    size: PropTypes.oneOf(CAPTIONED_ICON_SIZES),
    colour: PropTypes.oneOf(Object.values(Colours)),

    iconProps: IconPropType,
    iconSize: PropTypes.oneOf(Object.keys(Sizes.ICONS)),

    alignment: PropTypes.oneOf(CAPTIONED_ICON_HORIZONTAL_ALIGNMENT),
    weight: PropTypes.oneOf([ Typography.WEIGHTS.REGULAR, Typography.WEIGHTS.MEDIUM ]),
    className: PropTypes.string,
};


const MyFlexWrapper = styled.div`
    ${props => CAPTIONED_ICON_TYPOGRAPHIC_SPECS[props.size]};

    .caption {
        font-size: 1em; // Caption’s font-size affected by CaptionedIcon wrapper
        margin: 0;      // required here to overwrite Caption’s default
        line-height: 1.3;

        color: ${props => props.colour};
        font-weight: ${props => props.weight};
    }
`;

export default CaptionedIcon;
